/**
 * Next.js client instrumentation. This file runs once on the client before the
 * app hydrates, which is the modern, framework-blessed place to initialize
 * PostHog (replaces the manual `posthog.init` that used to live in a useEffect
 * inside app/providers.tsx).
 *
 * Runtime behavior is unchanged from the old provider init:
 *  - same config (identified_only, pageview/pageleave on, autocapture off,
 *    exception capture on, input/text masking on session recordings)
 *  - same internal-traffic opt-out: a `traffic_type=internal` cookie disables
 *    capturing entirely.
 *
 * Bootstrap: the client adopts the `bootstrap_distinct_id` cookie (seeded by middleware.ts)
 * as its PostHog distinct_id, so server-decided A/B variants (see lib/experiments/
 * getServerVariant.ts) bucket the same person client-side — exposure and conversion stay
 * attached to one user, with no re-fetch/flash.
 */

import posthog from 'posthog-js';
import { installTranslateCrashGuard } from '@/lib/translateCrashGuard';

// Runs before hydration: stop Google-Translate DOM mutations from crashing React
// with a removeChild/insertBefore NotFoundError (PostHog issue 019f91d3).
installTranslateCrashGuard();

/** Mirror the server + old-provider internal-traffic opt-out. */
function isInternalTraffic(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split(';')
    .some((cookie) => cookie.trim().startsWith('traffic_type=internal'));
}

/** Read a cookie value client-side (undefined if absent). */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(name + '='))
    ?.split('=')[1];
}

/**
 * Automated-browser filter.
 *
 * Headless crawlers/scrapers/test runners self-identify via `navigator.webdriver`
 * (real user browsers never set it). One such crawler — plain Chrome-on-Linux UA,
 * so PostHog's UA-based bot filter misses it — walks the whole site, mangles the
 * DOM before React hydrates (phantom React #418 "HTML" mismatches our code can't
 * cause), and pollutes analytics with uniform anonymous sessions. Drop everything
 * these browsers emit: errors, pageviews, the lot.
 */
function isAutomatedBrowser(): boolean {
  return typeof navigator !== 'undefined' && navigator.webdriver === true;
}

/**
 * Error Tracking noise filter.
 *
 * `capture_exceptions` auto-reports every unhandled error + promise rejection,
 * which means third-party junk that has nothing to do with our code lands in
 * Error Tracking and pages us. We drop three buckets, all confirmed not-our-bug:
 *   - Browser-extension injected errors: frames served from *-extension:// schemes,
 *     plus the classic "Object Not Found Matching Id … MethodName:update" async
 *     messaging rejection that a visitor's extension throws into window.
 *   - Third-party widget noise: the Tawk.to chat bundle (embed.tawk.to).
 *   - Benign browser noise: ResizeObserver loop warnings, and opaque cross-origin
 *     "Script error." with no actionable stack.
 *   - A third-party structured-data harvester choking on our (valid) JSON-LD
 *     array: `["@context"].toLowerCase`.
 *   - React #418 STRUCTURAL hydration mismatches (react.dev/errors/418?args[]=HTML):
 *     a JS-rendering crawler mangles the DOM between our HTML response and React
 *     hydration, producing element/structure mismatches our code can't cause (a
 *     full shared-chrome audit found zero structural nondeterminism). We drop the
 *     HTML class ONLY; the TEXT class (args[]=text — the shape a genuine code bug
 *     produces) is still captured. This catches the crawler even though it spoofs
 *     a real Chrome UA and hides `navigator.webdriver`. Tradeoff, accepted: a
 *     future structural hydration bug we introduce would also be hidden here.
 *   - Value-less promise rejections ("Non-Error promise rejection captured with
 *     value: undefined"): a promise rejected with no Error/stack/message — always
 *     an extension or third-party script (our pages produce zero on a clean load).
 *   - "Unexpected token '{'" SyntaxError: only browsers below our Chrome 109
 *     browserslist floor (e.g. Chrome 92) fail to parse the bundle's modern syntax;
 *     supported browsers parse it fine (verified). Matched narrowly on the `'{'`
 *     form so it can't swallow actionable `JSON.parse` "Unexpected token < in JSON"
 *     errors (which usually mean an API returned an error page).
 *
 * NOTE: the "Converting circular structure to JSON … __reactFiber" errors were
 * NOT third-party — the stack trace showed they came from our own error
 * boundaries handing a circular error object to posthog.captureException. Fixed
 * at the source in lib/captureError.ts, so there's no filter pattern for it: a
 * future circular-JSON should stay VISIBLE (it means capture is broken again).
 *
 * Conservative by design: only `$exception` events are ever dropped, matching is
 * plain substring/scheme containment, and any error inside the filter falls back
 * to KEEPING the event — a filter bug must never swallow a real error.
 */
const DROP_MESSAGE_PATTERNS = [
  'Object Not Found Matching Id', // browser-extension async-messaging rejection
  'MethodName:update', // same extension family
  '["@context"].toLowerCase', // 3rd-party JSON-LD harvester on our schema array
  'ResizeObserver loop', // benign layout-notification noise
  'Script error.', // opaque cross-origin 3rd-party script, no stack
  'ChunkLoadError', // stale bundle after a redeploy (old tab navigates) — not a code bug
  'Loading chunk', // same family ("Loading chunk N failed", "Loading CSS chunk")
  'errors/418?args[]=HTML', // React #418 structural hydration mismatch — crawler DOM-mangling (text-class #418 still captured)
  'Non-Error promise rejection captured with value', // promise rejected with no Error/stack/value — extension or 3rd-party junk, un-actionable
  "Unexpected token '{'", // SyntaxError only on browsers below our Chrome 109 floor; narrow form avoids matching JSON.parse "Unexpected token < in JSON"
];

const DROP_FRAME_SUBSTRINGS = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'tawk.to', // Tawk chat widget bundle (embed.tawk.to / cdn.tawk.to)
];

function isNoiseException(properties: Record<string, unknown>): boolean {
  const list = (properties?.['$exception_list'] as unknown[]) ?? null;
  if (!Array.isArray(list)) {
    // No structured list — match against a stringified blob of the exception props.
    const blob = JSON.stringify(properties?.['$exception_values'] ?? properties ?? '');
    return DROP_MESSAGE_PATTERNS.some((p) => blob.includes(p));
  }
  for (const ex of list as Array<Record<string, unknown>>) {
    const value = String(ex?.['value'] ?? '');
    const type = String(ex?.['type'] ?? '');
    if (DROP_MESSAGE_PATTERNS.some((p) => value.includes(p) || type.includes(p))) return true;
    const stack = ex?.['stacktrace'] as { frames?: Array<Record<string, unknown>> } | undefined;
    for (const frame of stack?.frames ?? []) {
      const file = String(frame?.['filename'] ?? frame?.['source'] ?? '');
      if (DROP_FRAME_SUBSTRINGS.some((h) => file.includes(h))) return true;
    }
  }
  return false;
}

const postHogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const postHogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

// Never send telemetry from a local dev build (`next dev`) or from internal traffic.
// Initialize with a dummy token + immediate opt-out so the PostHog instance still
// exists (usePostHog() etc. keep working) but nothing — pageviews, pageleave,
// exceptions, session recordings — ever reaches a real project. Deployed builds
// (Vercel preview + production, NODE_ENV=production) capture normally.
// Third-party iframe embeds (/embed/[name]/) get zero telemetry.
const onEmbedPath =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/embed/');

const suppressTelemetry =
  onEmbedPath || isInternalTraffic() || process.env.NODE_ENV !== 'production';

if (suppressTelemetry) {
  posthog.init('disabled', {
    api_host: postHogHost,
    loaded: (ph) => {
      ph.opt_out_capturing();
    },
  });
} else if (postHogKey) {
  posthog.init(postHogKey, {
    api_host: postHogHost,
    // Latest defaults bundle shipped by the installed posthog-js (1.336.x).
    // '2026-05-30' was requested but isn't a valid ConfigDefaults literal in
    // this version (union tops out at '2026-01-30'); using the newest valid one.
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: false,
    capture_exceptions: true, // Error Tracking: auto-capture unhandled errors + promise rejections
    // Two drop rules, both fail-open (any filter error keeps the event):
    //  1. Automated browsers (navigator.webdriver): drop ALL events.
    //  2. Third-party / browser-extension noise: drop $exception events only.
    // (Dev is already fully opted out above, so this only runs on deployed builds.)
    before_send: (event) => {
      try {
        if (isAutomatedBrowser()) {
          return null;
        }
        if (event && event.event === '$exception' && isNoiseException(event.properties ?? {})) {
          return null;
        }
      } catch {
        // Never let a filter bug swallow a real error.
      }
      return event;
    },
    disable_external_dependency_loading: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
    },
    // Adopt the middleware-seeded id so server-decided variants match client-side.
    bootstrap: { distinctID: readCookie('bootstrap_distinct_id'), isIdentifiedID: false },
  });
}
